import React, { useState, useEffect, useCallback } from 'react';
import { getAllAgents, createAgent, updateAgent, deleteAgent, updateAgentActiveStatus } from '../../api/admin';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';

// MUI Components
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Switch,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  Avatar,
  Divider,
  Alert,
} from '@mui/material';

// MUI Icons
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BadgeIcon from '@mui/icons-material/Badge';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const AgentManagement: React.FC = () => {
  const { admin } = useAuth();
  const [agents, setAgents] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalAgents, setTotalAgents] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusChangeLoading, setStatusChangeLoading] = useState<{[key: string]: boolean}>({});
  
  // Password visibility
  const [showAddPassword, setShowAddPassword] = useState<boolean>(false);
  const [showEditPassword, setShowEditPassword] = useState<boolean>(false);
  
  // New agent form state
  const [addDialogOpen, setAddDialogOpen] = useState<boolean>(false);
  const [newAgent, setNewAgent] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
  });
  const [formErrors, setFormErrors] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
  });
  
  // Edit agent form state
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [selectedAgent, setSelectedAgent] = useState<any>(null);
  const [editAgent, setEditAgent] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
  });
  
  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [agentToDelete, setAgentToDelete] = useState<string | null>(null);

  // Fetch agents
  const fetchAgents = async (search = searchTerm) => {
    try {
      setLoading(true);
      const response = await getAllAgents({
        search,
        page: page + 1,
        limit: rowsPerPage,
      });
      
      setAgents(response.agents);
      setTotalAgents(response.pagination.totalAgents);
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch agents');
    } finally {
      setLoading(false);
    }
  };

  // Create a debounced version of search
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      fetchAgents(searchValue);
    }, 500),
    [page, rowsPerPage]
  );

  // Initial fetch
  useEffect(() => {
    fetchAgents();
  }, [page, rowsPerPage]);

  // Handle search change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Open add agent dialog
  const handleOpenAddDialog = () => {
    setNewAgent({
      name: '',
      email: '',
      phone: '',
      password: '',
    });
    setFormErrors({
      name: '',
      email: '',
      phone: '',
      password: '',
    });
    setShowAddPassword(false);
    setAddDialogOpen(true);
  };

  // Close add agent dialog
  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
  };

  // Validate email format
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate phone format
  const isValidPhone = (phone: string): boolean => {
    // Basic validation for Indian phone numbers (10 digits)
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  // Validate add agent form
  const validateAddAgentForm = (): boolean => {
    const errors = {
      name: '',
      email: '',
      phone: '',
      password: '',
    };
    let isValid = true;

    if (!newAgent.name.trim()) {
      errors.name = 'Name is required';
      isValid = false;
    }

    if (!newAgent.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!isValidEmail(newAgent.email)) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }

    if (!newAgent.phone.trim()) {
      errors.phone = 'Phone is required';
      isValid = false;
    } else if (!isValidPhone(newAgent.phone)) {
      errors.phone = 'Please enter a valid 10-digit phone number';
      isValid = false;
    }

    // Validate password for new agent
    if (!newAgent.password.trim()) {
      errors.password = 'Password is required';
      isValid = false;
    } else if (newAgent.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  // Handle add agent form change
  const handleAddAgentFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewAgent({
      ...newAgent,
      [name]: value,
    });
  };

  // Submit add agent form
  const handleAddAgent = async () => {
    if (!validateAddAgentForm()) return;

    try {
      const response = await createAgent({
        name: newAgent.name,
        email: newAgent.email,
        phone: newAgent.phone,
        password: newAgent.password,
      });
      
      toast.success('Agent added successfully');
      handleCloseAddDialog();
      fetchAgents(); // Refresh the list
    } catch (error: any) {
      toast.error(error.message || 'An error occurred while adding the agent');
    }
  };

  // Open edit agent dialog
  const handleOpenEditDialog = (agent: any) => {
    setSelectedAgent(agent);
    setEditAgent({
      name: agent.name,
      email: agent.email,
      phone: agent.phone || '',
      password: '',
    });
    setShowEditPassword(false);
    setEditDialogOpen(true);
  };

  // Close edit agent dialog
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedAgent(null);
  };

  // Handle edit agent form change
  const handleEditAgentFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditAgent({
      ...editAgent,
      [name]: value,
    });
  };

  // Validate edit agent form
  const validateEditAgentForm = (): boolean => {
    const errors = {
      name: '',
      email: '',
      phone: '',
      password: '',
    };
    let isValid = true;

    if (!editAgent.name.trim()) {
      errors.name = 'Name is required';
      isValid = false;
    }

    if (!editAgent.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!isValidEmail(editAgent.email)) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }

    if (editAgent.phone && !isValidPhone(editAgent.phone)) {
      errors.phone = 'Please enter a valid 10-digit phone number';
      isValid = false;
    }

    // Password is optional for updates, but if provided, must be valid
    if (editAgent.password && editAgent.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  // Submit edit agent form
  const handleUpdateAgent = async () => {
    if (!validateEditAgentForm() || !selectedAgent) return;

    try {
      // Create an object with only the fields that should be updated
      const updateData: any = {
        name: editAgent.name,
        email: editAgent.email,
        phone: editAgent.phone,
      };
      
      // Only include password if it's been provided
      if (editAgent.password) {
        updateData.password = editAgent.password;
      }
      
      const response = await updateAgent(selectedAgent._id, updateData);
      
      toast.success('Agent updated successfully');
      handleCloseEditDialog();
      
      // Update the agent in the local state without refetching
      setAgents(agents.map(agent => 
        agent._id === selectedAgent._id 
          ? { ...agent, name: editAgent.name, email: editAgent.email, phone: editAgent.phone } 
          : agent
      ));
    } catch (error: any) {
      toast.error(error.message || 'An error occurred while updating the agent');
    }
  };

  // Handle active status change
  const handleActiveStatusChange = async (agentId: string, newStatus: boolean) => {
    try {
      setStatusChangeLoading({...statusChangeLoading, [agentId]: true});
      
      await updateAgentActiveStatus(agentId, newStatus);
      
      // Update the agent in the local state
      setAgents(agents.map(agent => 
        agent._id === agentId 
          ? { ...agent, isActive: newStatus } 
          : agent
      ));
      
      toast.success(`Agent ${newStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error: any) {
      toast.error(error.message || `Failed to ${newStatus ? 'activate' : 'deactivate'} agent`);
    } finally {
      setStatusChangeLoading({...statusChangeLoading, [agentId]: false});
    }
  };

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = (agentId: string) => {
    setAgentToDelete(agentId);
    setDeleteDialogOpen(true);
  };

  // Close delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setAgentToDelete(null);
  };

  // Delete agent
  const handleDeleteAgent = async () => {
    if (!agentToDelete) return;

    try {
      await deleteAgent(agentToDelete);
      
      toast.success('Agent deleted successfully');
      
      // Remove the agent from the local state
      setAgents(agents.filter(agent => agent._id !== agentToDelete));
      setTotalAgents(prev => prev - 1);
      
      handleCloseDeleteDialog();
    } catch (error: any) {
      toast.error(error.message || 'An error occurred while deleting the agent');
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, color: '#0F0F2D', mb: 4 }}>
        Agent Management
      </Typography>

      {/* Filters and Actions */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              placeholder="Search agents by name, email or phone..."
              variant="outlined"
              size="small"
              fullWidth
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Button 
              variant="contained" 
              startIcon={<PersonAddIcon />}
              onClick={handleOpenAddDialog}
              fullWidth
              sx={{
                backgroundColor: '#7065F0',
                '&:hover': {
                  backgroundColor: '#5A52C4',
                },
              }}
            >
              Add New Agent
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress size={60} sx={{ color: '#7065F0' }} />
        </Box>
      )}

      {/* Empty State */}
      {!loading && agents.length === 0 && (
        <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 2 }}>
          <PersonIcon sx={{ fontSize: 60, color: '#9CA3AF', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#374151', mb: 1 }}>
            No agents found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add your first agent by clicking the button below
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<PersonAddIcon />}
            onClick={handleOpenAddDialog}
            sx={{
              backgroundColor: '#7065F0',
              '&:hover': {
                backgroundColor: '#5A52C4',
              },
            }}
          >
            Add New Agent
          </Button>
        </Paper>
      )}

      {/* Agents Table */}
      {!loading && agents.length > 0 && (
        <TableContainer component={Paper} sx={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#F7F7FD' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agents.map((agent) => (
                <TableRow key={agent._id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: '#F0EDFF', 
                          color: '#7065F0', 
                          mr: 2,
                          width: 36,
                          height: 36, 
                        }}
                      >
                        {agent.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography variant="body2" fontWeight={500}>
                        {agent.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {agent.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {agent.phone || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={agent.isActive}
                          onChange={() => handleActiveStatusChange(agent._id, !agent.isActive)}
                          color="primary"
                          disabled={statusChangeLoading[agent._id]}
                        />
                      }
                      label={
                        <Chip
                          label={agent.isActive ? "Active" : "Inactive"}
                          size="small"
                          sx={{
                            backgroundColor: agent.isActive ? '#E1FCEF' : '#FEE4E2',
                            color: agent.isActive ? '#10B981' : '#F43F5E',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Edit Agent">
                        <IconButton 
                          size="small" 
                          onClick={() => handleOpenEditDialog(agent)}
                          sx={{ color: '#6B7280' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Agent">
                        <IconButton 
                          size="small" 
                          onClick={() => handleOpenDeleteDialog(agent._id)}
                          sx={{ color: '#F43F5E' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalAgents}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      {/* Add Agent Dialog */}
      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Add New Agent</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Name"
                  name="name"
                  fullWidth
                  value={newAgent.name}
                  onChange={handleAddAgentFormChange}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  fullWidth
                  value={newAgent.email}
                  onChange={handleAddAgentFormChange}
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Phone"
                  name="phone"
                  fullWidth
                  value={newAgent.phone}
                  onChange={handleAddAgentFormChange}
                  error={!!formErrors.phone}
                  helperText={formErrors.phone}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Password"
                  name="password"
                  type={showAddPassword ? 'text' : 'password'}
                  fullWidth
                  value={newAgent.password}
                  onChange={handleAddAgentFormChange}
                  error={!!formErrors.password}
                  helperText={formErrors.password}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowAddPassword(!showAddPassword)}
                          edge="end"
                        >
                          {showAddPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseAddDialog} color="inherit">Cancel</Button>
          <Button 
            onClick={handleAddAgent} 
            variant="contained"
            sx={{
              backgroundColor: '#7065F0',
              '&:hover': {
                backgroundColor: '#5A52C4',
              },
            }}
          >
            Add Agent
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Agent Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Edit Agent</DialogTitle>
        <DialogContent>
          {selectedAgent && (
            <Box component="form" sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Name"
                    name="name"
                    fullWidth
                    value={editAgent.name}
                    onChange={handleEditAgentFormChange}
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Email"
                    name="email"
                    type="email"
                    fullWidth
                    value={editAgent.email}
                    onChange={handleEditAgentFormChange}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Phone"
                    name="phone"
                    fullWidth
                    value={editAgent.phone}
                    onChange={handleEditAgentFormChange}
                    error={!!formErrors.phone}
                    helperText={formErrors.phone}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="New Password (Leave blank to keep current)"
                    name="password"
                    type={showEditPassword ? 'text' : 'password'}
                    fullWidth
                    value={editAgent.password}
                    onChange={handleEditAgentFormChange}
                    error={!!formErrors.password}
                    helperText={formErrors.password || 'Leave blank to keep current password'}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowEditPassword(!showEditPassword)}
                            edge="end"
                          >
                            {showEditPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseEditDialog} color="inherit">Cancel</Button>
          <Button 
            onClick={handleUpdateAgent} 
            variant="contained"
            sx={{
              backgroundColor: '#7065F0',
              '&:hover': {
                backgroundColor: '#5A52C4',
              },
            }}
          >
            Update Agent
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this agent? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteAgent} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentManagement; 